import { Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { Sort, SortDirection } from '@angular/material/sort';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';

import { ISnListQueryParameters, ISnListResponse, SortParameters } from '@lib/api';
import { SnUser, SnTeam, SnContentType, SnOrganization } from '@lib/api/entities';
import { OrganizationsAPI, SnTeamsAPI } from '@lib/api/services';
import { SnChannel } from '@lib/communication/twiliochat';
import { SnChannelsService } from '@lib/communication/twiliochat/services/channels.service';
import { FsPolicyGroupService, FsPolicyGroup } from '@lib/policy-groups';
import { TranslationModule } from '@lib/translation';
import { SnButtonModule } from '@lib/ui/button';
import { SnDataListComponent, SnDataListModule } from '@lib/ui/data-list';
import { SnIconModule } from '@lib/ui/icon';

import { FsDolpShareService } from '../../services';
import { DolpShareCrossorgTableComponent } from '../dolp-share-crossorg-table/dolp-share-crossorg-table.component';
import { DolpShareGroupTableComponent } from '../dolp-share-group-table/dolp-share-group-table.component';
import { DolpShareTableComponent } from '../dolp-share-table/dolp-share-table.component';

@Component({
  selector: 'sn-dolp-share',
  templateUrl: './dolp-share.component.html',
  styleUrls: ['./dolp-share.component.scss'],
  standalone: true,
  imports: [
    TranslationModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatOptionModule,
    MatRadioModule,
    MatSelectModule,
    MatTabsModule,
    MatTooltipModule,
    SnButtonModule,
    SnDataListModule,
    SnIconModule,
    DolpShareTableComponent,
    DolpShareGroupTableComponent,
    DolpShareCrossorgTableComponent
  ]
})
export class DolpShareComponent implements OnDestroy, OnInit {
  @ViewChild('listElement', { static: true })
  public readonly list: SnDataListComponent<SnUser>;
  public readonly loadData = this._loadData.bind(this);

  @ViewChild('permissionListElement', { static: true })
  public readonly permissionList: SnDataListComponent<FsPolicyGroup>;
  public readonly loadPermissionData = this._loadPermissionsData.bind(this);

  @ViewChild('crossOrgListElement')
  public readonly crossOrgList: SnDataListComponent<SnOrganization>;
  public readonly loadCrossOrgData = this._loadCrossOrgData.bind(this);

  public readonly searchControl: UntypedFormControl = new UntypedFormControl();

  public sort: string = 'email';
  public sortDirection: SortDirection = 'asc';
  public currentTab: string = 'Users';
  public originalAccess: [] = [];
  public isLawEnforcementPlan: boolean = true;
  public formModel: any = { mode: 'team' };
  public readonly form = new UntypedFormGroup({});
  public teams: SnTeam[] = [];
  public channels: SnChannel[] = [];
  public allCanView: boolean = false;
  public allCanEdit: boolean = false;
  public isOwned: boolean = false;

  public get columns(): string[] {
    return this._getColumns();
  }

  public get groupColumns(): string[] {
    return this._getGroupColumns();
  }

  public get crossOrgColumns(): string[] {
    return this._getCrossOrgColumns();
  }

  public get shouldShowOrganizationTab(): boolean {
    // Check if this share is for a citizen incident
    const isCitizenIncident = this.data?.inputItems?.meta?.citizenIncidentId;
    return this.isOwned && !isCitizenIncident;
  }

  public isLoading: boolean = false;

  private readonly _destroy$ = new Subject<void>();
  private _querySearch: string;
  private _userIds: string[];
  constructor(
    @Inject(MAT_DIALOG_DATA)
    public readonly data: {
      shareTitle: string;
      loadType: string;
      inputItems: any;
    },
    private readonly _dolpService: FsDolpShareService,
    private readonly _policyGroupService: FsPolicyGroupService,
    private readonly _organizationsAPI: OrganizationsAPI,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _teamsAPI: SnTeamsAPI,
    private readonly _channels: SnChannelsService,
    private readonly _dialogRef: MatDialogRef<DolpShareComponent>,
  ) {
    this._loadAccess();
    this.searchControl.valueChanges
      .pipe(debounceTime(1000), takeUntil(this._destroy$))
      .subscribe((v) => {
        this._querySearch = v;
        this._reloadData();
      });
    this.formModel.mode = 'team';
    
    this._teamsAPI.getTeamList({ pageSize: 'all' }).subscribe((response) => {
      this.teams = response.results;
      this._changeDetectorRef.detectChanges();
    });
    
    this._channels.getChannelList().subscribe((response) => {
      if ('results' in response) {
        this.channels = response.results;
      } else {
        this.channels = response as unknown as SnChannel[];
      }
      this._changeDetectorRef.detectChanges();
    });
  }

  public ngOnInit(): void {}

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public changeTab(change: MatTabChangeEvent): void {
    this.currentTab = change.tab.textLabel;
    this._changeDetectorRef.detectChanges();
  }

  public onSortChange(sort: Sort): void {
    this.sort = sort.active;
    this.sortDirection = sort.direction;

    this._reloadData();
  }

  public clear(): void {
    this.searchControl.reset(null);
  }

  public isEnforcementPlan(isSelected: MatCheckboxChange): void {
    this.isLawEnforcementPlan = isSelected.checked;
    this.crossOrgList.reference.reload();
  }

  public onModeChange(): void {
    this._reloadData();
  }

  public onTeamSelectChange(team: SnTeam): void {
    this._onTeamSelectChange(team);
  }

  public onChannelSelectChange(channel: SnChannel): void {
    this._onChannelSelectChange(channel);
  }

  public closeDialog(): void {
    this._dialogRef.close();
  }

  private _getColumns(): string[] {
    return ['fullName', 'email', 'lastActive', 'canView', 'canEdit', 'isAdmin'];
  }

  private _getGroupColumns(): string[] {
    return ['groupName', 'canView', 'canEdit', 'isAdmin'];
  }

  private _getCrossOrgColumns(): string[] {
    return ['groupName', 'share'];
  }

  private _loadAccess(): void {
    switch (this.data.loadType) {
      case SnContentType.MapViews:
        this._dolpService.getMapViewDolp(this.data.inputItems.id).subscribe((res) => {
          this.isOwned = res.isOwned;
          this.originalAccess = res;
          this._changeDetectorRef.detectChanges();
        });
        break;
      case SnContentType.Hotlists:
        this._dolpService.getLprHotlistDolp(this.data.inputItems.id).subscribe((res) => {
          this.isOwned = res.isOwned;
          this.originalAccess = res;
          this._changeDetectorRef.detectChanges();
        });
        break;
      default:
        break;
    }
  }

  private _loadData(params: ISnListQueryParameters): Observable<ISnListResponse<SnUser>> {
    params.search = this._querySearch;
    params.id__in = this._userIds?.join(',');
    return this._dolpService.getUsersBrief(this._prepareQueryParams(params));
  }

  private _loadPermissionsData(
    params: ISnListQueryParameters,
  ): Observable<ISnListResponse<FsPolicyGroup>> {
    params.search = this._querySearch;
    return this._policyGroupService.list(this._prepareQueryParams(params));
  }

  private _loadCrossOrgData(
    params: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnOrganization>> {
    params.plan__is_law_enforcement_plan = this.isLawEnforcementPlan;
    delete params.ordering;
    return this._organizationsAPI.getOrderedList(this._prepareQueryParams(params));
  }

  private _prepareQueryParams<T extends ISnListQueryParameters>(params: T): T {
    return {
      search: this._querySearch,
      ...params,
      pageSize: 'all',
      ordering: new SortParameters({
        active: this.sort,
        direction: this.sortDirection,
      }).convertedActive,
    };
  }

  private _reloadData(): void {
    this.list?.reference.reload();
    this.permissionList?.reference.reload();
    this.crossOrgList?.reference.reload();
  }

  private _onChannelSelectChange(channel: SnChannel): void {
    if (!channel) {
      this._userIds = [];
      this._reloadData();
      return;
    }
    const ids = channel.users.map((user) => user.id);
    this._userIds = ids;
    this._reloadData();
  }

  private _onTeamSelectChange(team: SnTeam): void {
    if (!team) {
      this._userIds = [];
      this._reloadData();
      return;
    }
    const a = team.callSigns.map((c) => c.assignments);
    const users = [].concat.apply([], a)?.map((t) => t.user);
    const ids = users.map((u) => u.id);
    this._userIds = ids;
    this._reloadData();
  }
}
